import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./NegativePaywall.module.scss"

/**
 * NegativePaywall — an original, self-contained ONBOARDING paywall.
 *
 * Divergence brief: claude-paywalls/_ledger.md; concept doc claude-paywalls/negative.md.
 * Active style directive this run: `focus on security and privacy`.
 *
 * Concept: a FILM NEGATIVE is the one image a stranger can't read. The first-run
 * user meets a bright LIGHTBOX with a strip of 35mm film holding their first
 * memories — every frame an amber, inverted NEGATIVE. Out in the open your photos
 * are already developed (anyone can read them); in your vault they rest as
 * negatives, and only YOUR key brings them to light. Drag the developing loupe
 * across the strip (or press "Develop my vault"): a warm develop-wave crosses the
 * film, each negative blooms to true colour, and an EYES ONLY · YOU frame-stamp
 * seats as the lock clasps. Auto-runs after a few idle seconds for passive
 * viewers; honours reduced motion.
 *
 *   - Layout:      a bright optical LIGHTBOX + horizontal 35mm FILM STRIP. No card
 *                  hero, no dossier, no keypad, no vault door.
 *   - Interaction: DRAG A DEVELOPING LOUPE along the strip — a directional,
 *                  object-anchored develop-wipe (not a static before/after divider).
 *   - Register:    analog-craft / quiet wonder — a photographer's ritual, not fear.
 *   - Art:         LIGHT lightbox — warm amber negatives, sprocket-hole film
 *                  borders, brand blue as the develop light. Deliberately bright.
 *   - Value frame: privacy via unreadable-to-others / readable-only-to-you.
 *
 * Honest: maps to real protections (Face ID / PIN, kept out of the camera roll,
 * private encrypted backup). Strictly additive — no data-loss claim, no fake
 * urgency. Yearly is the featured, default plan.
 */

// The strip — reuse existing personal-memory photos (public/byheart/mNN.jpg).
const STRIP = ["m04", "m09", "m02", "m14", "m07", "m11"]

// What your key actually protects — real product features, stated plainly.
const PROTECTS = [
  { k: "lock", label: "Locked with Face ID & PIN" },
  { k: "roll", label: "Kept out of your camera roll" },
  { k: "cloud", label: "Private, encrypted backup" },
]

type PlanKey = "yearly" | "monthly"

const NegativePaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  // develop progress 0..1 across the whole strip.
  const [progress, setProgress] = useState(reduceMotion ? 1 : 0)
  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [showClose, setShowClose] = useState(false)

  const stripRef = useRef<HTMLDivElement>(null)
  const touched = useRef(false)
  const rafRef = useRef<number>(0)
  const timers = useRef<number[]>([])
  const pushTimer = (id: number) => timers.current.push(id)

  const stopAuto = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = 0
  }

  // Auto-run the develop demo once, after a few idle seconds, if untouched.
  const autoDevelop = useCallback(() => {
    if (touched.current || reduceMotion) return
    native.logMixpanelEvent("html_negative_autodevelop")
    const DURATION = 1900
    let start = 0
    const step = (t: number) => {
      if (touched.current) return
      if (!start) start = t
      const p = Math.min(1, (t - start) / DURATION)
      // ease-out for a settling "develop" feel
      setProgress(1 - Math.pow(1 - p, 2))
      if (p < 1) rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
  }, [reduceMotion])

  useEffect(() => {
    if (reduceMotion) {
      setShowClose(true)
      setProgress(1)
      return
    }
    pushTimer(window.setTimeout(() => setShowClose(true), 2400))
    pushTimer(window.setTimeout(autoDevelop, 3200))
    return () => {
      stopAuto()
      timers.current.forEach((id) => window.clearTimeout(id))
      timers.current = []
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Drag the developing loupe: progress = pointer x across the strip.
  const developAt = (clientX: number) => {
    const el = stripRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const p = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    // develop is monotonic — the film doesn't un-develop as you sweep back
    setProgress((prev) => Math.max(prev, p))
  }

  const beginDrag = (clientX: number) => {
    if (reduceMotion) return
    if (!touched.current) {
      touched.current = true
      stopAuto()
      native.logMixpanelEvent("html_negative_develop_drag")
    }
    developAt(clientX)
  }

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    beginDrag(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return
    beginDrag(e.clientX)
  }

  const developNow = () => {
    touched.current = true
    stopAuto()
    setProgress(1)
  }

  const fullyDeveloped = progress >= 0.999
  // per-frame reveal as the wave passes each frame's centre.
  const frameDeveloped = (i: number) => progress * STRIP.length >= i + 0.5

  const selectedProduct = plan === "yearly" ? yearlyProduct : monthlyProduct
  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    native.logMixpanelEvent("html_negative_subscribe", { plan })
    if (selectedProduct) native.subscribe(selectedProduct)
  }
  const openTerms = () => native.postNativeEvent("terms")
  const openPrivacy = () => native.postNativeEvent("privacy")

  return (
    <div className={`${styles.root} ${fullyDeveloped ? styles.rootDev : ""}`}>
      <div className={styles.glow} aria-hidden="true" />

      {/* ---------- header ---------- */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="11.4" r="2.1" fill="currentColor" />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
          onClick={handleClose}
          aria-label="Close"
          tabIndex={showClose ? 0 : -1}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ---------- title ---------- */}
      <div className={styles.intro}>
        <span className={styles.eyebrow}>Only your key develops them</span>
        <h1 className={styles.title}>
          Developed for
          <br />
          your eyes only
        </h1>
        <p className={styles.sub}>
          Out in the open, your photos are already developed — open to anyone
          who picks up your phone. In your vault they rest as negatives. Only
          your key brings them to light.
        </p>
      </div>

      {/* ---------- lightbox + film strip ---------- */}
      <div className={styles.lightbox}>
        <div className={styles.lightboxLabel}>
          <span>LIGHT&nbsp;TABLE</span>
          <span className={styles.roll}>ROLL 01 · PPV</span>
        </div>

        <div
          className={styles.strip}
          ref={stripRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          role="slider"
          aria-label="Develop your memories"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress * 100)}
        >
          <div className={styles.perfTop} aria-hidden="true" />
          <div className={styles.frames}>
            {STRIP.map((id, i) => {
              const dev = frameDeveloped(i)
              return (
                <div
                  key={id}
                  className={`${styles.frame} ${dev ? styles.frameDev : ""}`}
                >
                  <div
                    className={styles.pos}
                    style={{ backgroundImage: `url(/byheart/${id}.jpg)` }}
                  />
                  <div
                    className={styles.neg}
                    style={{ backgroundImage: `url(/byheart/${id}.jpg)` }}
                  />
                  <span className={styles.frameNo}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className={styles.bloom} aria-hidden="true" />
                </div>
              )
            })}
          </div>
          <div className={styles.perfBot} aria-hidden="true" />

          {/* developing loupe — rides the develop wave */}
          {!reduceMotion && (
            <motion.div
              className={styles.loupe}
              aria-hidden="true"
              style={{ left: `${progress * 100}%` }}
              animate={{ opacity: fullyDeveloped ? 0 : 1 }}
              transition={{ duration: 0.4 }}
            >
              <span className={styles.loupeRing} />
              <span className={styles.loupeGrip} />
            </motion.div>
          )}

          {/* eyes-only stamp seats once fully developed */}
          <motion.div
            className={styles.stamp}
            aria-hidden={!fullyDeveloped}
            initial={false}
            animate={
              fullyDeveloped
                ? { opacity: 1, scale: 1, rotate: -8 }
                : { opacity: 0, scale: 1.5, rotate: -22 }
            }
            transition={{ type: "spring", stiffness: 320, damping: 16 }}
          >
            <span className={styles.stampInner}>
              EYES&nbsp;ONLY
              <em>· YOU ·</em>
            </span>
          </motion.div>
        </div>

        <div className={styles.hint} aria-live="polite">
          {fullyDeveloped ? (
            <span className={styles.hintDone}>
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4 4 10-10.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Developed — and only you hold the key
            </span>
          ) : (
            <button className={styles.developBtn} onClick={developNow}>
              <span className={styles.developArrow} aria-hidden="true">
                ↔
              </span>
              Drag to develop your vault
            </button>
          )}
        </div>
      </div>

      {/* ---------- protections ---------- */}
      <div className={styles.protects}>
        {PROTECTS.map((p, i) => (
          <span
            key={p.k}
            className={`${styles.protect} ${fullyDeveloped ? styles.protectOn : ""}`}
            style={{ transitionDelay: `${120 + i * 90}ms` }}
          >
            <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
              <path
                d="M5 12.5l4 4 10-10.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {p.label}
          </span>
        ))}
      </div>

      {/* ---------- pricing (yearly featured) ---------- */}
      <div className={styles.pricing}>
        <button
          className={`${styles.plan} ${styles.planYear} ${
            plan === "yearly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.ribbon}>SAVE {savingsPct}%</span>
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planMeta}>
              <span className={styles.planName}>12 months</span>
              <span className={styles.planNote}>
                7 days free · then {yearlyPerMonth}/mo
              </span>
            </span>
          </span>
          <span className={styles.planRight}>
            <span className={styles.planPrice}>{yearlyPrice}</span>
            <span className={styles.planUnit}>/year</span>
          </span>
        </button>

        <button
          className={`${styles.plan} ${styles.planMonth} ${
            plan === "monthly" ? styles.planActive : ""
          }`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.planLeft}>
            <span className={styles.radio} aria-hidden="true" />
            <span className={styles.planName}>Monthly</span>
          </span>
          <span className={styles.planMonthPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaMain}>
          {plan === "yearly" ? "Try 7 days free" : "Continue"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? `Then ${yearlyPrice}/year · cancel anytime`
            : `${monthlyPrice}/month · cancel anytime`}
        </span>
      </button>

      <div className={styles.legal}>
        <button onClick={openTerms}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={openPrivacy}>Privacy</button>
        <span aria-hidden="true">·</span>
        <span>Auto-renews until cancelled</span>
      </div>
    </div>
  )
}

export default NegativePaywall
